import { useQuery } from "@tanstack/react-query";
import { useState, useMemo, type PropsWithChildren, useEffect } from "react";

import { useFeatureIsOn } from "@/app/featureFlags";
import { isUserEligibleQueryOptions } from "@/hooks/useIsUserEligible";

import { PaymentContext, type PaymentType } from "./PaymentContext";

const MIN_BNPL_INSTALLMENTS = 1;
const MAX_BNPL_INSTALLMENTS = 10;

export type PaymentProviderProps = PropsWithChildren;

export const PaymentContextProvider = ({ children }: PaymentProviderProps) => {
  const isBnplFeatureEnabled = useFeatureIsOn("bnpl");

  const {
    data,
    isSuccess: isUserEligibleSuccess,
    isLoading,
    refetch,
    isFetching,
  } = useQuery(isUserEligibleQueryOptions("", isBnplFeatureEnabled));

  const [paymentType, setPaymentType] = useState<PaymentType>("standard");
  const [selectedInstallments, setSelectedInstallments] = useState<number>(
    MAX_BNPL_INSTALLMENTS,
  );
  const availableInstallments = useMemo(() => {
    if (data?.isEligible) {
      const minInstallments = data.minCountOfMonth || MIN_BNPL_INSTALLMENTS;
      // for now, we want to limit to 10 installments max
      const maxInstallments = Math.min(
        data.maxCountOfMonth,
        MAX_BNPL_INSTALLMENTS,
      );

      return Array.from(
        { length: maxInstallments - minInstallments + 1 },
        (_, i) => i + minInstallments,
      );
    }

    return [];
  }, [data]);

  const maxAvailableInstallments = useMemo(
    () =>
      availableInstallments[availableInstallments.length - 1] ||
      MAX_BNPL_INSTALLMENTS,
    [availableInstallments],
  );

  useEffect(() => {
    const handleEvent: WindowEventHandler<"offerselected"> = () => {
      setPaymentType("standard");
      setSelectedInstallments(maxAvailableInstallments);
    };

    window.addEventListener("offerselected", handleEvent);

    return () => {
      window.removeEventListener("offerselected", handleEvent);
    };
  }, [data?.isEligible, isBnplFeatureEnabled, maxAvailableInstallments]);

  const sessionId =
    isUserEligibleSuccess && data.isEligible ? data.sessionId : null;

  const contextValue = useMemo(
    () => ({
      paymentType,
      selectedInstallments,
      availableInstallments,
      maxAvailableInstallments,
      sessionId,
      handlePaymentTypeChange: setPaymentType,
      setInstallments: setSelectedInstallments,
      isEligibilityLoading: isLoading,
      isEligibilityFetching: isFetching,
      isEligible: data?.isEligible === undefined ? null : data?.isEligible,
      refreshSession: refetch,
    }),
    [
      availableInstallments,
      data?.isEligible,
      isFetching,
      isLoading,
      maxAvailableInstallments,
      paymentType,
      refetch,
      selectedInstallments,
      sessionId,
    ],
  );

  return (
    <PaymentContext.Provider value={contextValue}>
      {children}
    </PaymentContext.Provider>
  );
};
